import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { enableMapSet } from 'immer'

// Enable Immer MapSet plugin for Map and Set support
enableMapSet()

// Types for real-time state management
export interface WebSocketConnection {
  id: string
  socket: WebSocket
  status: ConnectionStatus
  reconnectAttempts: number
  lastConnected: Date | null
  config: ConnectionConfig
}

export interface ConnectionConfig {
  url: string
  protocols?: string[]
  autoReconnect?: boolean
  maxReconnectAttempts?: number
  reconnectInterval?: number
  heartbeatInterval?: number
}

export interface RealtimeMessage {
  id: string
  channel: string
  type: string
  payload: unknown
  timestamp: Date
  processed: boolean
}

export interface Subscription {
  id: string
  channel: string
  callback: (data: unknown) => void
  active: boolean
}

export enum ConnectionStatus {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  RECONNECTING = 'reconnecting',
  ERROR = 'error'
}

export interface RealtimeState {
  // Connection management
  connections: Map<string, WebSocketConnection>
  subscriptions: Map<string, Subscription>
  messageQueue: RealtimeMessage[]

  // State synchronization
  syncedData: Map<string, unknown>
  lastSyncTimestamp: Date | null
  syncInProgress: boolean

  // Offline support
  isOnline: boolean
  offlineQueue: RealtimeMessage[]

  // Connection status
  globalStatus: ConnectionStatus
  connectionErrors: Map<string, Error>

  // Actions
  createConnection: (id: string, config: ConnectionConfig) => Promise<void>
  destroyConnection: (id: string) => void
  subscribe: (channel: string, callback: (data: unknown) => void) => () => void
  unsubscribe: (channel: string) => void
  sendMessage: (channel: string, type: string, payload: unknown) => void
  syncWithBackend: () => Promise<void>
  processMessageQueue: () => void
  setOnlineStatus: (online: boolean) => void
  reconnectAll: () => Promise<void>
  getConnectionStatus: (id?: string) => ConnectionStatus
  clearErrors: () => void
}

// Exponential backoff utility
const calculateBackoffDelay = (attempt: number, baseDelay = 1000, maxDelay = 30000): number => {
  const exponentialDelay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay)
  const jitter = Math.random() * 0.1 * exponentialDelay // Add 10% jitter
  return exponentialDelay + jitter
}

// Message deduplication utility
const createMessageId = (channel: string, type: string, payload: unknown): string => {
  const content = JSON.stringify({ channel, type, payload })
  const timestamp = Date.now()
  return `${channel}-${type}-${btoa(content).slice(0, 8)}-${timestamp}`
}

// WebSocket creation utility
const createWebSocket = (config: ConnectionConfig): WebSocket => {
  if (config.protocols) {
    return new WebSocket(config.url, config.protocols)
  }
  return new WebSocket(config.url)
}

export const useRealtimeStore = create<RealtimeState>()
  (subscribeWithSelector(
    immer((set, get) => ({
      // Initial state
      connections: new Map(),
      subscriptions: new Map(),
      messageQueue: [],
      syncedData: new Map(),
      lastSyncTimestamp: null,
      syncInProgress: false,
      isOnline: navigator.onLine,
      offlineQueue: [],
      globalStatus: ConnectionStatus.DISCONNECTED,
      connectionErrors: new Map(),

      // Create and manage WebSocket connections
      createConnection: async (id: string, config: ConnectionConfig) => {
        set((state) => {
          // Remove existing connection if any
          const existingConnection = state.connections.get(id)
          if (existingConnection) {
            existingConnection.socket.close()
            state.connections.delete(id)
          }

          // Create new connection
          const connection: WebSocketConnection = {
            id,
            socket: createWebSocket(config),
            status: ConnectionStatus.CONNECTING,
            reconnectAttempts: 0,
            lastConnected: null,
            config
          }

          // Setup event handlers
          setupWebSocketHandlers(connection, set, get)

          state.connections.set(id, connection)
          state.globalStatus = ConnectionStatus.CONNECTING
        })
      },

      // Destroy connection
      destroyConnection: (id: string) => {
        set((state) => {
          const connection = state.connections.get(id)
          if (connection) {
            connection.socket.close()
            state.connections.delete(id)
            state.connectionErrors.delete(id)
          }

          // Update global status
          if (state.connections.size === 0) {
            state.globalStatus = ConnectionStatus.DISCONNECTED
          }
        })
      },

      // Subscribe to channel
      subscribe: (channel: string, callback: (data: unknown) => void) => {
        const subscriptionId = `${channel}-${Date.now()}-${Math.random()}`

        set((state) => {
          const subscription: Subscription = {
            id: subscriptionId,
            channel,
            callback,
            active: true
          }
          state.subscriptions.set(subscriptionId, subscription)
        })

        // Subscribe to channel on all active connections
        const connections = get().connections
        connections.forEach(connection => {
          if (connection.status === ConnectionStatus.CONNECTED) {
            connection.socket.send(JSON.stringify({
              type: 'subscribe',
              channel
            }))
          }
        })

        // Return unsubscribe function
        return () => {
          set((state) => {
            const subscription = state.subscriptions.get(subscriptionId)
            if (subscription) {
              subscription.active = false
              state.subscriptions.delete(subscriptionId)
            }
          })

          // Unsubscribe from channel on all connections
          const currentConnections = get().connections
          currentConnections.forEach(connection => {
            if (connection.status === ConnectionStatus.CONNECTED) {
              connection.socket.send(JSON.stringify({
                type: 'unsubscribe',
                channel
              }))
            }
          })
        }
      },

      // Unsubscribe from channel
      unsubscribe: (channel: string) => {
        set((state) => {
          const toRemove: string[] = []
          state.subscriptions.forEach((subscription, id) => {
            if (subscription.channel === channel) {
              toRemove.push(id)
            }
          })
          toRemove.forEach(id => state.subscriptions.delete(id))
        })

        // Unsubscribe from channel on all connections
        const connections = get().connections
        connections.forEach(connection => {
          if (connection.status === ConnectionStatus.CONNECTED) {
            connection.socket.send(JSON.stringify({
              type: 'unsubscribe',
              channel
            }))
          }
        })
      },

      // Send message to channel
      sendMessage: (channel: string, type: string, payload: unknown) => {
        const message: RealtimeMessage = {
          id: createMessageId(channel, type, payload),
          channel,
          type,
          payload,
          timestamp: new Date(),
          processed: false
        }

        const state = get()

        if (!state.isOnline) {
          // Queue message for when back online
          set((state) => {
            state.offlineQueue.push(message)
          })
          return
        }

        // Send to all connected sockets
        state.connections.forEach(connection => {
          if (connection.status === ConnectionStatus.CONNECTED) {
            try {
              connection.socket.send(JSON.stringify({
                ...message,
                timestamp: message.timestamp.toISOString()
              }))
            } catch (error) {
              console.error('Failed to send message:', error)
              set((state) => {
                state.offlineQueue.push(message)
              })
            }
          }
        })
      },

      // Sync with backend - only when there are active connections and subscriptions
      // This is a fallback polling mechanism for when WebSockets miss messages
      syncWithBackend: async () => {
        const state = get()

        // Skip sync if:
        // - No WebSocket connections are configured
        // - No active subscriptions to sync
        // - Not connected (globalStatus !== 'connected')
        // - Offline
        if (state.connections.size === 0) {
          // No WebSocket connections configured - sync is not needed
          return
        }

        if (state.subscriptions.size === 0) {
          // No subscriptions to sync data for
          return
        }

        if (state.globalStatus !== ConnectionStatus.CONNECTED) {
          // Not connected - sync would fail anyway
          return
        }

        if (!state.isOnline) {
          // Browser is offline
          return
        }

        set((state) => {
          state.syncInProgress = true
        })

        try {
          const params = new URLSearchParams({
            lastSync: get().lastSyncTimestamp?.toISOString() || '',
            subscriptions: Array.from(get().subscriptions.values()).map(s => s.channel).join(',')
          })
          // Use absolute API URL to ensure requests go through APISIX gateway
          const apiUrl = import.meta.env.VITE_API_URL
          if (!apiUrl) {
            throw new Error('VITE_API_URL is not configured')
          }
          const response = await fetch(`${apiUrl}/v1/realtime/sync?${params.toString()}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include' // Include cookies for session-based auth
          })

          if (response.ok) {
            const syncData = await response.json()
            set((state) => {
              // Update synced data
              if (syncData.data) {
                Object.entries(syncData.data).forEach(([key, value]) => {
                  state.syncedData.set(key, value)
                })
              }
              state.lastSyncTimestamp = new Date()
            })
          }
          // Note: Non-200 responses are silently ignored as the endpoint may not be implemented yet
        } catch (error) {
          // Network errors are logged but don't break the app
          // This is expected when the realtime sync endpoint is not available
          if (import.meta.env.DEV) {
            console.debug('Realtime sync not available:', error)
          }
        } finally {
          set((state) => {
            state.syncInProgress = false
          })
        }
      },

      // Process message queue
      processMessageQueue: () => {
        set((state) => {
          const unprocessedMessages = state.messageQueue.filter(msg => !msg.processed)

          unprocessedMessages.forEach(message => {
            // Find matching subscriptions
            state.subscriptions.forEach(subscription => {
              if (subscription.channel === message.channel && subscription.active) {
                try {
                  // Serialize payload to avoid passing Immer proxy to callbacks
                  const payload = JSON.parse(JSON.stringify(message.payload))
                  subscription.callback(payload)
                  message.processed = true
                } catch (error) {
                  console.error('Error processing message:', error, message)
                }
              }
            })
          })

          // Clean up old processed messages (keep last 100)
          state.messageQueue = state.messageQueue
            .filter(msg => !msg.processed ||
              (new Date().getTime() - msg.timestamp.getTime()) < 60000)
            .slice(-100)
        })
      },

      // Set online status
      setOnlineStatus: (online: boolean) => {
        set((state) => {
          state.isOnline = online

          if (online && state.offlineQueue.length > 0) {
            // Clone the offline queue to avoid Immer draft issues
            // Serialize and deserialize to ensure no Immer proxies remain
            const queuedMessages = state.offlineQueue.map(msg => ({
              channel: msg.channel,
              type: msg.type,
              payload: JSON.parse(JSON.stringify(msg.payload))
            }))
            state.offlineQueue = []

            // Capture sendMessage reference before setTimeout to avoid proxy revocation issues
            const sendMessage = get().sendMessage

            // Resend queued messages
            setTimeout(() => {
              queuedMessages.forEach(message => {
                sendMessage(message.channel, message.type, message.payload)
              })
            }, 1000)
          }
        })
      },

      // Reconnect all connections
      reconnectAll: async () => {
        const state = get()
        const reconnectionPromises: Promise<void>[] = []

        state.connections.forEach(connection => {
          if (connection.config.autoReconnect !== false &&
              connection.reconnectAttempts < (connection.config.maxReconnectAttempts ?? 5)) {

            const promise = new Promise<void>((resolve) => {
              const delay = calculateBackoffDelay(
                connection.reconnectAttempts,
                connection.config.reconnectInterval
              )

              setTimeout(() => {
                get().createConnection(connection.id, connection.config)
                resolve()
              }, delay)
            })

            reconnectionPromises.push(promise)
          }
        })

        await Promise.all(reconnectionPromises)
      },

      // Get connection status
      getConnectionStatus: (id?: string) => {
        const state = get()

        if (id) {
          return state.connections.get(id)?.status ?? ConnectionStatus.DISCONNECTED
        }

        return state.globalStatus
      },

      // Clear all errors
      clearErrors: () => {
        set((state) => {
          state.connectionErrors.clear()
        })
      }
    }))
  ))

// WebSocket event handlers
function setupWebSocketHandlers(
  connection: WebSocketConnection,
  set: any,
  get: () => RealtimeState
) {
  const socket = connection.socket as WebSocket

  socket.onopen = () => {
    set((state: RealtimeState) => {
      const conn = state.connections.get(connection.id)
      if (conn) {
        conn.status = ConnectionStatus.CONNECTED
        conn.lastConnected = new Date()
        conn.reconnectAttempts = 0
        state.globalStatus = ConnectionStatus.CONNECTED
        state.connectionErrors.delete(connection.id)
      }
    })

    // Resubscribe to all channels
    const subscriptions = get().subscriptions
    subscriptions.forEach(subscription => {
      if (subscription.active) {
        socket.send(JSON.stringify({
          type: 'subscribe',
          channel: subscription.channel
        }))
      }
    })
  }

  socket.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data)
      const message: RealtimeMessage = {
        id: data.id || createMessageId(data.channel, data.type, data.payload),
        channel: data.channel,
        type: data.type,
        payload: data.payload,
        timestamp: data.timestamp ? new Date(data.timestamp) : new Date(),
        processed: false
      }

      set((state: RealtimeState) => {
        // Check for duplicates
        const duplicate = state.messageQueue.find(msg => msg.id === message.id)
        if (!duplicate) {
          state.messageQueue.push(message)
        }
      })

      // Process messages immediately
      get().processMessageQueue()
    } catch (error) {
      console.error('Error parsing WebSocket message:', error, event.data)
    }
  }

  socket.onclose = (event) => {
    set((state: RealtimeState) => {
      const conn = state.connections.get(connection.id)
      if (conn) {
        conn.status = event.wasClean ? ConnectionStatus.DISCONNECTED : ConnectionStatus.ERROR

        if (!event.wasClean && conn.config.autoReconnect !== false) {
          conn.status = ConnectionStatus.RECONNECTING
          conn.reconnectAttempts += 1
        }
      }
    })

    // Auto-reconnect if enabled
    if (!event.wasClean && connection.config.autoReconnect !== false) {
      const delay = calculateBackoffDelay(
        connection.reconnectAttempts,
        connection.config.reconnectInterval
      )

      setTimeout(() => {
        if (connection.reconnectAttempts < (connection.config.maxReconnectAttempts ?? 5)) {
          get().createConnection(connection.id, connection.config)
        }
      }, delay)
    }
  }

  socket.onerror = () => {
    set((state: RealtimeState) => {
      const conn = state.connections.get(connection.id)
      if (conn) {
        conn.status = ConnectionStatus.ERROR
        state.connectionErrors.set(connection.id, new Error('WebSocket error'))
      }
    })
  }
}

// Online/offline event listeners
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    useRealtimeStore.getState().setOnlineStatus(true)
    useRealtimeStore.getState().reconnectAll()
  })

  window.addEventListener('offline', () => {
    useRealtimeStore.getState().setOnlineStatus(false)
  })

  // Page visibility for connection management
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      // Page became visible - attempt to sync with backend
      // The syncWithBackend function will check if sync is actually needed
      // (has connections, subscriptions, is connected, and is online)
      useRealtimeStore.getState().syncWithBackend()
    }
  })
}

// Cleanup function for React components
export const useRealtimeCleanup = () => {
  return () => {
    const store = useRealtimeStore.getState()
    store.connections.forEach((_, id) => {
      store.destroyConnection(id)
    })
  }
}

// Hook for easy connection management
export const useRealtimeConnection = (id: string, config: ConnectionConfig) => {
  const store = useRealtimeStore()

  React.useEffect(() => {
    store.createConnection(id, config)

    return () => {
      store.destroyConnection(id)
    }
  }, [id, config.url])

  return {
    status: store.getConnectionStatus(id),
    isConnected: store.getConnectionStatus(id) === ConnectionStatus.CONNECTED
  }
}

// React import for hooks
import React from 'react'